export type ThemeModeSetting = 'light' | 'dark';

export type UserSettings = {
  ui: {
    maxTAccountsPerRow: number;
    showDecimals: boolean;
    formatNumbersWithCommas: boolean;
    themeMode: ThemeModeSetting;
  };
  export: {
    entriesSeparateTab: boolean;
    formatNumbersWithCommas: boolean;
    maxTAccountsPerRow: number;
  };
};

export const DEFAULT_USER_SETTINGS: UserSettings = {
  ui: {
    maxTAccountsPerRow: 4,
    showDecimals: false,
    formatNumbersWithCommas: false,
    themeMode: 'light',
  },
  export: {
    entriesSeparateTab: true,
    formatNumbersWithCommas: false,
    maxTAccountsPerRow: 4,
  },
};

function clampMaxPerRow(value: unknown, defaultValue = 4): number {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return defaultValue;
  }

  return Math.max(1, Math.min(10, Math.round(parsedValue)));
}

export function normalizeUserSettings(input: unknown): UserSettings {
  const source = (input && typeof input === 'object') ? input as Record<string, unknown> : {};
  const uiSource = (source.ui && typeof source.ui === 'object') ? source.ui as Record<string, unknown> : {};
  const exportSource = (source.export && typeof source.export === 'object') ? source.export as Record<string, unknown> : {};

  const themeMode = uiSource.theme_mode === 'dark' || uiSource.themeMode === 'dark'
    ? 'dark'
    : 'light';

  return {
    ui: {
      maxTAccountsPerRow: clampMaxPerRow(
        uiSource.maxTAccountsPerRow ?? uiSource.max_taccounts_per_row,
        DEFAULT_USER_SETTINGS.ui.maxTAccountsPerRow,
      ),
      showDecimals: Boolean(uiSource.showDecimals ?? uiSource.show_decimals),
      formatNumbersWithCommas: Boolean(uiSource.formatNumbersWithCommas ?? uiSource.format_numbers_with_commas),
      themeMode,
    },
    export: {
      entriesSeparateTab: (exportSource.entriesSeparateTab ?? exportSource.entries_separate_tab) !== false,
      formatNumbersWithCommas: Boolean(exportSource.formatNumbersWithCommas ?? exportSource.format_numbers_with_commas),
      maxTAccountsPerRow: clampMaxPerRow(
        exportSource.maxTAccountsPerRow ?? exportSource.max_taccounts_per_row,
        DEFAULT_USER_SETTINGS.export.maxTAccountsPerRow,
      ),
    },
  };
}

export function serializeUserSettings(settings: UserSettings): Record<string, unknown> {
  return {
    ui: {
      max_taccounts_per_row: clampMaxPerRow(settings.ui.maxTAccountsPerRow, DEFAULT_USER_SETTINGS.ui.maxTAccountsPerRow),
      show_decimals: Boolean(settings.ui.showDecimals),
      format_numbers_with_commas: Boolean(settings.ui.formatNumbersWithCommas),
      theme_mode: settings.ui.themeMode === 'dark' ? 'dark' : 'light',
    },
    export: {
      entries_separate_tab: settings.export.entriesSeparateTab !== false,
      format_numbers_with_commas: Boolean(settings.export.formatNumbersWithCommas),
      max_taccounts_per_row: clampMaxPerRow(settings.export.maxTAccountsPerRow, DEFAULT_USER_SETTINGS.export.maxTAccountsPerRow),
    },
  };
}
